import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Check, ChevronLeft, ChevronRight, Eye, RotateCw, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Standard tangram pieces (local coordinates, origin at each piece's own frame).
const TANGRAM_PIECES = [
  { id: 'large-tri-1', name: 'Large Triangle', color: '#EF4444', points: [[0, 0], [80, 0], [40, 40]] },
  { id: 'large-tri-2', name: 'Large Triangle', color: '#F97316', points: [[0, 0], [80, 0], [40, 40]] },
  { id: 'medium-tri', name: 'Medium Triangle', color: '#EAB308', points: [[0, 0], [56, 0], [28, 28]] },
  { id: 'small-tri-1', name: 'Small Triangle', color: '#22C55E', points: [[0, 0], [40, 0], [20, 20]] },
  { id: 'small-tri-2', name: 'Small Triangle', color: '#3B82F6', points: [[0, 0], [40, 0], [20, 20]] },
  { id: 'square', name: 'Square', color: '#8B5CF6', points: [[0, 0], [28, 0], [28, 28], [0, 28]] },
  { id: 'parallelogram', name: 'Parallelogram', color: '#EC4899', points: [[0, 20], [20, 0], [60, 0], [40, 20]] },
];

// Pieces that are geometrically identical are interchangeable when solving, so
// the win check matches by shape group, not by exact id.
const SHAPE_KEY = {
  'large-tri-1': 'L', 'large-tri-2': 'L', 'medium-tri': 'M',
  'small-tri-1': 'S', 'small-tri-2': 'S', 'square': 'SQ', 'parallelogram': 'P',
};
// Rotation period (deg) at which each shape looks identical to itself.
const SYMMETRY = { L: 360, M: 360, S: 360, SQ: 90, P: 180 };

const pointsToPath = (points) =>
  points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ') + ' Z';

const getBounds = (points) => {
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  return { width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
};

const TEMPLATE = Object.fromEntries(
  TANGRAM_PIECES.map((p) => [p.id, { ...p, path: pointsToPath(p.points), bounds: getBounds(p.points) }])
);

// The shared transform for BOTH the target ghost and the live pieces, so a
// piece whose (x, y, rotation) equals a slot's overlaps it exactly.
const pieceTransform = (id, x, y, rotation) => {
  const b = TEMPLATE[id].bounds;
  return `translate(${x - b.width / 2}, ${y - b.height / 2}) rotate(${rotation}, ${b.width / 2}, ${b.height / 2})`;
};

// Each puzzle's target is the union of the 7 pieces in their solution slots, so
// the outline is GUARANTEED to be fillable by the pieces (unlike a hand-drawn
// outline). Solve = get every piece onto a matching slot.
const PUZZLES = [
  {
    id: 'p1', name: 'Puzzle 1', emoji: '🧩',
    solution: [
      { id: 'large-tri-1', x: 130, y: 90, rotation: 135 },
      { id: 'large-tri-2', x: 90, y: 130, rotation: 225 },
      { id: 'medium-tri', x: 158, y: 158, rotation: 315 },
      { id: 'small-tri-1', x: 170, y: 90, rotation: 45 },
      { id: 'small-tri-2', x: 130, y: 130, rotation: 45 },
      { id: 'square', x: 150, y: 110, rotation: 0 },
      { id: 'parallelogram', x: 108, y: 168, rotation: 0 },
    ],
  },
  {
    id: 'p2', name: 'Puzzle 2', emoji: '🧩',
    solution: [
      { id: 'large-tri-1', x: 120, y: 90, rotation: 0 },
      { id: 'large-tri-2', x: 120, y: 130, rotation: 180 },
      { id: 'medium-tri', x: 170, y: 110, rotation: 90 },
      { id: 'small-tri-1', x: 90, y: 90, rotation: 0 },
      { id: 'small-tri-2', x: 90, y: 130, rotation: 180 },
      { id: 'square', x: 90, y: 110, rotation: 0 },
      { id: 'parallelogram', x: 60, y: 110, rotation: 90 },
    ],
  },
  {
    id: 'p3', name: 'Puzzle 3', emoji: '🧩',
    solution: [
      { id: 'large-tri-1', x: 130, y: 150, rotation: 0 },
      { id: 'large-tri-2', x: 190, y: 150, rotation: 0 },
      { id: 'medium-tri', x: 160, y: 90, rotation: 180 },
      { id: 'small-tri-1', x: 120, y: 190, rotation: 0 },
      { id: 'small-tri-2', x: 200, y: 190, rotation: 0 },
      { id: 'square', x: 160, y: 175, rotation: 0 },
      { id: 'parallelogram', x: 160, y: 120, rotation: 0 },
    ],
  },
  {
    id: 'p4', name: 'Puzzle 4', emoji: '🧩',
    solution: [
      { id: 'large-tri-1', x: 130, y: 130, rotation: 0 },
      { id: 'large-tri-2', x: 190, y: 130, rotation: 0 },
      { id: 'medium-tri', x: 160, y: 80, rotation: 180 },
      { id: 'small-tri-1', x: 95, y: 165, rotation: 45 },
      { id: 'small-tri-2', x: 225, y: 165, rotation: 315 },
      { id: 'square', x: 160, y: 150, rotation: 0 },
      { id: 'parallelogram', x: 160, y: 175, rotation: 0 },
    ],
  },
];

const VIEW_W = 320;
const VIEW_H = 240;
const POS_TOL = 24; // px in viewBox units
const ROT_TOL = 24; // deg

const angleClose = (a, b, period) => {
  let d = Math.abs((((a - b) % period) + period) % period);
  d = Math.min(d, period - d);
  return d <= ROT_TOL;
};

const TameTangram = () => {
  const navigate = useNavigate();
  const [currentPuzzleIndex, setCurrentPuzzleIndex] = useState(0);
  const [pieces, setPieces] = useState([]);
  const [showSolution, setShowSolution] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const canvasRef = useRef(null);

  const currentPuzzle = PUZZLES[currentPuzzleIndex];

  const initializePuzzle = useCallback(() => {
    setPieces(
      TANGRAM_PIECES.map((piece) => ({
        id: piece.id,
        x: 0,
        y: 0,
        rotation: 0,
        placed: false,
        locked: false,
      }))
    );
    setIsComplete(false);
    setShowSolution(false);
    setSelectedPiece(null);
  }, []);

  useEffect(() => {
    initializePuzzle();
  }, [currentPuzzleIndex, initializePuzzle]);

  // Greedily lock any placed piece that sits on a matching, still-free slot,
  // snapping it exactly into place. Pure function of (pieces, puzzle).
  const computeLocks = useCallback(
    (arr) => {
      const slots = currentPuzzle.solution.map((s) => ({
        key: SHAPE_KEY[s.id],
        x: s.x,
        y: s.y,
        rotation: ((s.rotation % 360) + 360) % 360,
      }));
      const next = arr.map((p) => ({ ...p, locked: false }));
      const used = new Set();
      for (let s = 0; s < slots.length; s++) {
        const slot = slots[s];
        let best = -1;
        let bestD = Infinity;
        for (let i = 0; i < next.length; i++) {
          const p = next[i];
          if (used.has(i) || !p.placed) continue;
          if (SHAPE_KEY[p.id] !== slot.key) continue;
          if (!angleClose(p.rotation, slot.rotation, SYMMETRY[slot.key])) continue;
          const dx = Math.abs(p.x - slot.x);
          const dy = Math.abs(p.y - slot.y);
          if (dx <= POS_TOL && dy <= POS_TOL && dx + dy < bestD) {
            best = i;
            bestD = dx + dy;
          }
        }
        if (best >= 0) {
          used.add(best);
          next[best] = { ...next[best], x: slot.x, y: slot.y, rotation: slot.rotation, locked: true };
        }
      }
      return next;
    },
    [currentPuzzle]
  );

  // Win check reacts to the actual pieces state (no stale closure / setTimeout).
  useEffect(() => {
    if (pieces.length === 7 && pieces.every((p) => p.locked)) {
      setIsComplete(true);
    }
  }, [pieces]);

  const updatePiece = (pieceId, patch) => {
    setPieces((prev) =>
      computeLocks(
        prev.map((piece) => (piece.id === pieceId ? { ...piece, ...patch } : piece))
      )
    );
  };

  const placePiece = (pieceId) => {
    updatePiece(pieceId, { x: 160, y: 120, placed: true });
    setSelectedPiece(pieceId);
  };

  const rotatePiece = (pieceId) => {
    setPieces((prev) => {
      const target = prev.find((p) => p.id === pieceId);
      if (!target) return prev;
      const nextRot = (target.rotation + 45) % 360;
      return computeLocks(prev.map((p) => (p.id === pieceId ? { ...p, rotation: nextRot } : p)));
    });
  };

  const removePiece = (pieceId) => {
    setPieces((prev) =>
      computeLocks(
        prev.map((piece) =>
          piece.id === pieceId ? { ...piece, x: 0, y: 0, rotation: 0, placed: false, locked: false } : piece
        )
      )
    );
    setSelectedPiece(null);
  };

  // Correct client → viewBox mapping that accounts for preserveAspectRatio
  // "xMidYMid meet" letterboxing (uniform scale + centring offset).
  const handleCanvasClick = (e) => {
    if (!selectedPiece || !canvasRef.current) return;
    const svg = canvasRef.current.querySelector('svg');
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const scale = Math.min(rect.width / VIEW_W, rect.height / VIEW_H);
    const offsetX = (rect.width - VIEW_W * scale) / 2;
    const offsetY = (rect.height - VIEW_H * scale) / 2;
    const x = (e.clientX - rect.left - offsetX) / scale;
    const y = (e.clientY - rect.top - offsetY) / scale;
    if (x < 0 || y < 0 || x > VIEW_W || y > VIEW_H) return;
    updatePiece(selectedPiece, { x, y });
  };

  const nextPuzzle = () => setCurrentPuzzleIndex((prev) => (prev + 1) % PUZZLES.length);
  const prevPuzzle = () => setCurrentPuzzleIndex((prev) => (prev - 1 + PUZZLES.length) % PUZZLES.length);

  const placedCount = pieces.filter((p) => p.placed).length;
  const lockedCount = pieces.filter((p) => p.locked).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FEF3C7] via-[#FDE68A] to-[#FCD34D] relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-orange-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-6">
        <header className="flex justify-between items-center mb-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-4 py-2 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-[#2D3748]">Tame the Tangram</h1>
          <button
            onClick={initializePuzzle}
            className="bg-white text-[#4A5568] px-4 py-2 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm text-sm"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </header>

        {/* Puzzle selector */}
        <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
          <button onClick={prevPuzzle} className="bg-white p-2 rounded-full shadow-sm hover:bg-slate-100 transition-colors">
            <ChevronLeft className="w-5 h-5 text-[#4A5568]" />
          </button>
          <div className="bg-white rounded-xl px-5 py-2 shadow-sm flex items-center gap-3">
            <span className="text-3xl">{currentPuzzle.emoji}</span>
            <div>
              <p className="text-xs text-[#4A5568]">Fill the shape</p>
              <p className="text-lg font-bold text-[#2D3748]">{currentPuzzle.name}</p>
            </div>
          </div>
          <button onClick={nextPuzzle} className="bg-white p-2 rounded-full shadow-sm hover:bg-slate-100 transition-colors">
            <ChevronRight className="w-5 h-5 text-[#4A5568]" />
          </button>
          <button
            onClick={() => setShowSolution((v) => !v)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold transition-all duration-300 ml-2 ${
              showSolution ? 'bg-purple-500 text-white' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
            }`}
          >
            <Eye className="w-4 h-4" />
            {showSolution ? 'Hide Solution' : 'See Solution'}
          </button>
        </div>

        {/* Instructions + progress */}
        <div className="bg-white/80 rounded-xl p-3 mb-4 text-center">
          <p className="text-sm text-[#4A5568]">
            <strong>How to play:</strong> Click a piece to select it, click on the grey shape to move it, and use{' '}
            <RotateCw className="w-4 h-4 inline" /> to rotate. Pieces snap into place when they fit.
          </p>
          <p className="text-xs text-[#4A5568] mt-1">{lockedCount} / 7 pieces in place</p>
        </div>

        {/* Canvas */}
        <div
          ref={canvasRef}
          onClick={handleCanvasClick}
          className="bg-white rounded-2xl shadow-lg mb-4 relative cursor-crosshair"
          style={{ height: '350px' }}
        >
          <svg className="w-full h-full" viewBox="0 0 320 240" preserveAspectRatio="xMidYMid meet">
            {/* Target silhouette = the solution pieces, so it is always fillable */}
            {currentPuzzle.solution.map((sol, idx) => (
              <path
                key={`target-${idx}`}
                d={TEMPLATE[sol.id].path}
                transform={pieceTransform(sol.id, sol.x, sol.y, sol.rotation)}
                fill="#E2E8F0"
                stroke="#94A3B8"
                strokeWidth={1.5}
              />
            ))}

            {/* Optional coloured solution overlay */}
            {showSolution &&
              currentPuzzle.solution.map((sol, idx) => (
                <path
                  key={`sol-${idx}`}
                  d={TEMPLATE[sol.id].path}
                  transform={pieceTransform(sol.id, sol.x, sol.y, sol.rotation)}
                  fill={TEMPLATE[sol.id].color}
                  fillOpacity={0.35}
                  stroke={TEMPLATE[sol.id].color}
                  strokeWidth={1.5}
                  strokeDasharray="4,2"
                />
              ))}

            {/* Placed pieces */}
            {pieces
              .filter((p) => p.placed)
              .map((piece) => {
                const tmpl = TEMPLATE[piece.id];
                return (
                  <g
                    key={piece.id}
                    transform={pieceTransform(piece.id, piece.x, piece.y, piece.rotation)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPiece(piece.id);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <path
                      d={tmpl.path}
                      fill={tmpl.color}
                      fillOpacity={piece.locked ? 1 : 0.92}
                      stroke={selectedPiece === piece.id ? '#000' : piece.locked ? '#fff' : '#334155'}
                      strokeWidth={selectedPiece === piece.id ? 3 : 2}
                      className="drop-shadow-md"
                    />
                  </g>
                );
              })}
          </svg>

          {/* Controls for the selected, placed piece */}
          {selectedPiece && pieces.find((p) => p.id === selectedPiece)?.placed && (
            <div className="absolute top-2 right-2 flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  rotatePiece(selectedPiece);
                }}
                className="bg-blue-500 text-white p-2 rounded-full shadow-lg hover:bg-blue-600 transition-colors"
                title="Rotate 45°"
              >
                <RotateCw className="w-5 h-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removePiece(selectedPiece);
                }}
                className="bg-red-500 text-white p-2 rounded-full shadow-lg hover:bg-red-600 transition-colors"
                title="Return piece to tray"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Tray */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4">
          <p className="text-xs text-[#4A5568] mb-3 text-center font-medium">
            {placedCount === 7 ? 'All pieces placed — rotate and nudge them to fit!' : 'Click a piece to add it to the board'}
          </p>
          <div className="flex flex-wrap justify-center gap-3 min-h-[60px]">
            {pieces
              .filter((p) => !p.placed)
              .map((piece) => {
                const tmpl = TEMPLATE[piece.id];
                return (
                  <button
                    key={piece.id}
                    onClick={() => placePiece(piece.id)}
                    className="p-3 rounded-xl transition-all duration-200 border-2 border-slate-200 bg-slate-50 hover:border-slate-400 hover:shadow-md"
                    title={`Add the ${tmpl.name}`}
                  >
                    <svg width="50" height="40" viewBox={`-5 -5 ${tmpl.bounds.width + 10} ${tmpl.bounds.height + 10}`}>
                      <path d={tmpl.path} fill={tmpl.color} stroke="#fff" strokeWidth="1" />
                    </svg>
                    <p className="text-[10px] text-center text-[#4A5568] mt-1 truncate w-12">{tmpl.name}</p>
                  </button>
                );
              })}
          </div>
        </div>

        {/* Completion */}
        <AnimatePresence>
          {isComplete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl"
              >
                <div className="text-6xl mb-4">{currentPuzzle.emoji}</div>
                <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-[#2D3748] mb-2">Well Done!</h2>
                <p className="text-[#4A5568] mb-6">You solved {currentPuzzle.name}!</p>
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={initializePuzzle}
                    className="bg-slate-200 text-[#4A5568] px-6 py-3 rounded-full font-semibold hover:bg-slate-300 transition-colors"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={nextPuzzle}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg"
                  >
                    Next Puzzle
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TameTangram;
